import React, { CSSProperties, useContext } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useRepriceAccount, useRepriceReasons } from '@app/sdk';
import { FormContainer, Input, Select, SelectOption } from '@app/shared';
import { RepriceAccountRequest, RepriceType } from '@app/models';
import { formatDate } from '@app/utilities';
import { AccountContext } from '../AccountContext';

type RepriceAccountFormModel = {
  newRate: string;
  effectiveDate?: Date;
  repriceType: RepriceType;
  reason: string;
};

const repriceTypeOptions: SelectOption[] = [
  { label: 'Reprice Up', value: 'RepriceUp' },
  { label: 'Reprice Down', value: 'RepriceDown' },
];

export default function RepriceAccount({ onRepriced }: { onRepriced: () => void }) {
  const flexRowSpaceBetween: CSSProperties = { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' };
  const { accountId, billingDate } = useContext(AccountContext);
  const formContext = useForm<RepriceAccountFormModel>();
  const { data: repriceReasons, isLoading: isLoadingRepriceReasons } = useRepriceReasons();
  const { mutateAsync: repriceAccount, isPending } = useRepriceAccount();
  const queryClient = useQueryClient();

  const reasonOptions = Array.isArray(repriceReasons)
    ? repriceReasons
        .map((reason) => (typeof reason === 'string' ? reason : reason.reason))
        .filter(Boolean)
        .map((reason) => ({
          label: reason,
          value: reason,
        }))
    : [];

  async function onSubmit(data: RepriceAccountFormModel) {
    const request: RepriceAccountRequest = {
      newRate: Number(data.newRate),
      repriceType: data.repriceType,
      reason: data.reason,
    };

    if (data.effectiveDate) {
      request.effectiveDate = formatDate(data.effectiveDate, "yyyy-MM-dd'T'00:00:00xxx");
    }

    await repriceAccount({
      accountId,
      request,
    });

    toast.success('Account repriced.');
    await queryClient.invalidateQueries({ queryKey: ['accountInterestRates', accountId] });
    await queryClient.invalidateQueries({ queryKey: ['accountEffectiveRates', accountId] });
    onRepriced();
    formContext.reset();
  }

  return (
    <div className="ui segment">
      <div className="ui header">Reprice Account</div>
      <div className="content">
        <FormProvider {...formContext}>
          <FormContainer isSubmitting={isPending} onSubmit={onSubmit} disableButtons={isLoadingRepriceReasons}>
            <Input
              defaultValue=""
              label="New Rate"
              name="newRate"
              type="number"
              step="0.00001"
              min="0"
              placeholder="e.g. 0.365"
              options={{
                validate: (value) => {
                  if (value === undefined || value === null || value === '') {
                    return 'New rate is required';
                  }

                  return /^\d+(\.\d{1,5})?$/.test(value) || 'Maximum 5 decimal places';
                },
              }}
            />
          <Controller
            name="effectiveDate"
            control={formContext.control}
            render={({ field }) => (
              <div className="sixteen wide column" style={flexRowSpaceBetween}>
                <label className="ui label extra large datepicker-label">Effective date</label>
                <DatePicker
                  name="effectiveDate"
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  dateFormat="dd/MM/yyyy"
                  minDate={billingDate}
                  placeholderText="Effective date"
                  isClearable
                />
              </div>
            )}
          />
            <Select
              defaultValue=""
              label="Reprice Type"
              name="repriceType"
              options={{ required: 'Reprice type is required' }}
              selectOptions={repriceTypeOptions}
            />
            <Select
              defaultValue=""
              label="Reason"
              name="reason"
              options={{ required: 'Reason is required' }}
              selectOptions={reasonOptions}
              disabled={isLoadingRepriceReasons}
            />
          </FormContainer>
        </FormProvider>
      </div>
    </div>
  );
}
